import React, { useEffect, useState } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import { Spinner } from 'react-bootstrap'
import { Button, Card, CardBody, CardHeader, Col, Form, FormGroup, FormText, Input, Row } from 'reactstrap'
import Select from 'react-select'

import Header from '../../components/Headers/Header'
import FileInput from '../../components/molecules/FileInput'
import { notify } from '../../utils/notify'
import { ToastContainer } from 'react-toastify'
import { api } from '../../utils'
import { useFileUpload } from '../../hooks'
import SetTitle from '../../utils/set_title'
import { DEFAULT_QUESTION_PROMPT } from './defaultPrompt'
import { getLLMVisibilityClientName } from './clienteDisplay'
import ClienteField from './ClienteField'
import LocalizationFields from './LocalizationFields'
import auth from '../../auth/service'
import './LLMVisibilityScheduledForm.css'

const LLMVisibilityScheduledForm = () => {
  SetTitle("Scheduled LLM Visibility");
  const isWebranking = auth.userGroup === 'Webranking'
  const { id } = useParams();
  const history = useHistory();
  const isEdit = !!id;
  const { upload, loading } = useFileUpload();

  const [name, setName] = useState("");
  const [brand, setBrand] = useState("");
  const [clienti, setClienti] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState("");
  const [country, setCountry] = useState("IT");
  const [language, setLanguage] = useState("it");
  const [file, setFile] = useState(null);
  const [useGpt, setUseGpt] = useState(false);
  const [useGemini, setUseGemini] = useState(false);
  const [usePerplexity, setUsePerplexity] = useState(false);
  const [customPrompt, setCustomPrompt] = useState(DEFAULT_QUESTION_PROMPT);
  const [startDate, setStartDate] = useState(new Date().toISOString().slice(0, 10));
  const [scheduleType, setScheduleType] = useState("M");
  const [interval, setIntervalValue] = useState(1);
  const [questionsOnly, setQuestionsOnly] = useState(false);
  const [questionsGenerated, setQuestionsGenerated] = useState(false);
  const [active, setActive] = useState(true);
  const [taggingToolId, setTaggingToolId] = useState("");
  const [taggingToolReports, setTaggingToolReports] = useState([]);

  const loadDetails = async () => {
    if (!id) {
      return;
    }
    const res = await api.get(`llm-visibility-schedules/${id}/`);
    const data = res.data;
    setName(data.name || "");
    setBrand(getLLMVisibilityClientName(data));
    setSelectedClientId(data?.cliente?.id ? String(data.cliente.id) : "");
    setCountry(data.country || "IT");
    setLanguage(data.language || "it");
    setUseGpt(!!data.gpt);
    setUseGemini(!!data.gemini);
    setUsePerplexity(!!data.perplexity);
    setCustomPrompt(data.custom_prompt || DEFAULT_QUESTION_PROMPT);
    setStartDate(data.start_date || new Date().toISOString().slice(0, 10));
    setScheduleType(data.schedule_type || "M");
    setIntervalValue(data.interval || 1);
    setQuestionsOnly(!!data.questions_only);
    setQuestionsGenerated(!!data.questions_generated);
    setActive(!!data.active);
    setTaggingToolId(data.connected_tagging || "");
  };

  useEffect(() => {
    if (isWebranking) {
      api
        .get("clienti/")
        .then((res) => setClienti(Array.isArray(res.data) ? res.data : res.data?.results || []))
        .catch(() => notify("Could not load clienti.", "warning"));
    }

    api
      .get("tagging-tool/reports/")
      .then((res) => setTaggingToolReports(res.data || []))
      .catch(() => notify("Could not load Tagging Tool reports.", "warning"));

    loadDetails().catch(() => notify("Failed to load schedule.", "error"));
  }, [id, isWebranking]);

  const submit = async (event) => {
    if (event) {
      event.preventDefault();
    }
    if (!name || (!isEdit && !file)) {
      notify("Inserisci nome e file.", "error");
      return;
    }
    if (isWebranking && !selectedClientId) {
      notify("Seleziona un cliente.", "error");
      return;
    }
    if (!country || !language) {
      notify("Seleziona country e lingua.", "error");
      return;
    }
    if (!useGpt && !useGemini && !usePerplexity) {
      notify("Seleziona almeno un modello LLM.", "error");
      return;
    }
    if (customPrompt && !customPrompt.includes("{keyword}")) {
      notify("Il custom prompt deve contenere {keyword}.", "error");
      return;
    }
    if (!interval || Number(interval) < 1) {
      notify("L'intervallo deve essere almeno 1.", "error");
      return;
    }

    const formData = new FormData();
    formData.append("name", name);
    formData.append("brand", brand);
    if (selectedClientId) {
      formData.append("cliente", selectedClientId);
    }
    formData.append("country", country);
    formData.append("language", language);
    formData.append("gpt", useGpt);
    formData.append("gemini", useGemini);
    formData.append("perplexity", usePerplexity);
    formData.append("custom_prompt", customPrompt);
    formData.append("start_date", startDate);
    formData.append("schedule_type", scheduleType);
    formData.append("interval", Number(interval));
    formData.append("questions_only", questionsOnly);
    formData.append("active", active);
    if (taggingToolId) {
      formData.append("connected_tagging", taggingToolId);
    }
    if (file) {
      formData.append("file", file);
    }

    try {
      if (isEdit) {
        await upload("patch", `llm-visibility-schedules/${id}/`, formData);
      } else {
        await upload("post", "llm-visibility-schedules/", formData);
      }
      notify("Schedule salvata correttamente.", "success");
      setTimeout(() => history.push("/llm-visibility-scheduled"), 500);
    } catch {
      notify("Errore nel salvataggio della schedule.", "error");
    }
  };

  const activateAnswers = async () => {
    try {
      await api.post(`llm-visibility-schedules/${id}/activate_answers/`);
      notify("Schedule attivata per le risposte schedulate.", "success");
      loadDetails();
    } catch (err) {
      notify(err.response?.data?.error || "Impossibile attivare le risposte.", "error");
    }
  };

  const generateQuestions = async () => {
    try {
      await api.post(`llm-visibility-schedules/${id}/generate_questions/`, {
        force_rerun: questionsGenerated,
      });
      notify("Generazione domande avviata.", "success");
      loadDetails();
    } catch (err) {
      notify(err.response?.data?.error || "Impossibile avviare la generazione domande.", "error");
    }
  };

  return (
    <Header
      iconClass="far fa-calendar-alt text-white"
      title="Scheduled LLM Visibility"
      description="Con Questions Only generi solo domande. Il run schedulato delle risposte parte solo dopo attivazione esplicita."
    >
      <div className="pb-5">
        <Card className="bg-secondary shadow llm-visibility-scheduled-card">
          <CardHeader className="bg-white border-0">
            <Row className="align-items-center">
              <Col md={10}>
                <h1 className="mb-0">{isEdit ? "Edit Schedule" : "New Schedule"}</h1>
              </Col>
              <Col md={2} style={{ textAlign: "right" }}>
                <Button color="primary" type="button" onClick={() => history.push("/llm-visibility-scheduled")}>
                  &larr; Back
                </Button>
              </Col>
            </Row>
          </CardHeader>
          <CardBody>
            {loading ? (
              <div className="text-center" role="status" aria-live="polite">
                <Spinner animation="border" role="status" />
                <span className="sr-only">Caricamento schedule LLM Visibility...</span>
              </div>
            ) : (
              <Form onSubmit={submit}>
                <h6 className="heading-small text-muted mb-3">Configurazione base</h6>
                <Row>
                  <Col md={6}>
                    <FormGroup>
                      <label htmlFor="schedule-name" className="form-control-label">Nome schedule</label>
                      <Input id="schedule-name" value={name} onChange={(e) => setName(e.target.value)} required />
                    </FormGroup>
                    {isWebranking ? (
                      <ClienteField
                        clienti={clienti}
                        selectedClientId={selectedClientId}
                        setSelectedClientId={setSelectedClientId}
                        setBrand={setBrand}
                      />
                    ) : (
                      <FormGroup>
                        <label htmlFor="schedule-brand" className="form-control-label">Brand</label>
                        <Input id="schedule-brand" value={brand} onChange={(e) => setBrand(e.target.value)} />
                      </FormGroup>
                    )}
                    <LocalizationFields
                      country={country}
                      setCountry={setCountry}
                      language={language}
                      setLanguage={setLanguage}
                      countryId="schedule-country"
                      languageId="schedule-language"
                    />
                    <FormGroup>
                      <label htmlFor="schedule-start-date" className="form-control-label">Start date</label>
                      <Input id="schedule-start-date" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} required />
                      <FormText>
                        La schedule si ancora alla data selezionata.
                      </FormText>
                    </FormGroup>
                    <FormGroup>
                      <label htmlFor="schedule-type" className="form-control-label">Schedule type</label>
                      <Input
                        id="schedule-type"
                        type="select"
                        value={scheduleType}
                        onChange={(e) => setScheduleType(e.target.value)}
                      >
                        <option value="M">Monthly</option>
                        <option value="W">Weekly</option>
                      </Input>
                    </FormGroup>
                    <FormGroup>
                      <label htmlFor="schedule-interval" className="form-control-label">
                        Interval ({scheduleType === "W" ? "weeks" : "months"})
                      </label>
                      <Input
                        id="schedule-interval"
                        type="number"
                        min="1"
                        step="1"
                        value={interval}
                        onChange={(e) => setIntervalValue(e.target.value)}
                        required
                      />
                      <FormText>
                        Esempio: con valore 2, la schedule viene eseguita ogni {scheduleType === "W" ? "2 settimane" : "2 mesi"}.
                      </FormText>
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FileInput
                      onFileChange={setFile}
                      accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                      helpText={
                        isEdit ? "Carica un nuovo file solo se vuoi sostituire le keyword." : "File Excel con colonna Keyword."
                      }
                      required={!isEdit}
                    />
                  </Col>
                </Row>

                <hr className="my-3" />
                <h6 className="heading-small text-muted mb-3">LLM e collegamenti</h6>
                <Row className="align-items-start">
                  <Col md={6} className="mb-3 mb-md-0">
                    <FormGroup>
                      <label className="form-control-label">LLM da usare</label>
                      <div className="llm-visibility-scheduled-check-grid">
                        <div className="llm-visibility-scheduled-check-item">
                          <input className="llm-visibility-scheduled-check-input" id="sched-gpt" type="checkbox" checked={useGpt} onChange={() => setUseGpt(!useGpt)} />
                          <label className="mb-0" htmlFor="sched-gpt">OpenAI GPT</label>
                        </div>
                        <div className="llm-visibility-scheduled-check-item">
                          <input className="llm-visibility-scheduled-check-input" id="sched-gemini" type="checkbox" checked={useGemini} onChange={() => setUseGemini(!useGemini)} />
                          <label className="mb-0" htmlFor="sched-gemini">Google Gemini</label>
                        </div>
                        <div className="llm-visibility-scheduled-check-item">
                          <input className="llm-visibility-scheduled-check-input" id="sched-perplexity" type="checkbox" checked={usePerplexity} onChange={() => setUsePerplexity(!usePerplexity)} />
                          <label className="mb-0" htmlFor="sched-perplexity">Perplexity</label>
                        </div>
                      </div>
                      <FormText>Seleziona almeno un modello.</FormText>
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup>
                      <label htmlFor="schedule-tagging-tool" className="form-control-label">Connetti Tagging Tool (opzionale)</label>
                      <Select
                        inputId="schedule-tagging-tool"
                        name="schedule-tagging-tool"
                        aria-label="Connetti Tagging Tool"
                        options={taggingToolReports.map((r) => ({ value: r.id, label: r.name }))}
                        isClearable
                        onChange={(e) => setTaggingToolId(e?.value || "")}
                        value={taggingToolId ? { value: taggingToolId, label: taggingToolReports.find((r) => r.id === taggingToolId)?.name } : null}
                      />
                      <FormText>Collegando un report esistente puoi riusare i tag gia disponibili.</FormText>
                    </FormGroup>
                  </Col>
                </Row>

                <hr className="my-3" />
                <h6 className="heading-small text-muted mb-3">Prompt e opzioni di esecuzione</h6>
                <Row>
                  <Col md={12}>
                    <FormGroup>
                      <label htmlFor="schedule-custom-prompt" className="form-control-label">Custom prompt</label>
                      <Input
                        id="schedule-custom-prompt"
                        type="textarea"
                        rows="12"
                        className="llm-visibility-scheduled-prompt"
                        value={customPrompt}
                        onChange={(e) => setCustomPrompt(e.target.value)}
                      />
                      <FormText>Deve contenere {"{keyword}"}.</FormText>
                    </FormGroup>
                  </Col>
                </Row>

                <Row>
                  <Col md={6} className="mb-3 mb-md-0">
                    <FormGroup className="llm-visibility-scheduled-toggle">
                      <div className="llm-visibility-scheduled-toggle-head">
                        <label className="form-control-label mb-0" htmlFor="schedule-questions-only">Questions only</label>
                        <input className="llm-visibility-scheduled-toggle-input" id="schedule-questions-only" type="checkbox" checked={questionsOnly} onChange={() => setQuestionsOnly(!questionsOnly)} />
                      </div>
                      <FormText>
                        Se attivo, verranno generate solo le domande e il run delle risposte non partira finche non viene attivato manualmente.
                      </FormText>
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup className="llm-visibility-scheduled-toggle">
                      <div className="llm-visibility-scheduled-toggle-head">
                        <label className="form-control-label mb-0" htmlFor="schedule-active">Active</label>
                        <input className="llm-visibility-scheduled-toggle-input" id="schedule-active" type="checkbox" checked={active} onChange={() => setActive(!active)} />
                      </div>
                      <FormText>Disattiva la schedule per metterla in pausa senza eliminarla.</FormText>
                    </FormGroup>
                  </Col>
                </Row>

                {isEdit && questionsOnly && (
                  <Row className="mt-3">
                    <Col md={12}>
                      <div className="alert alert-info">
                        Questions mode attivo. Questions generated: <b>{questionsGenerated ? "Yes" : "No"}</b>.
                      </div>
                    </Col>
                  </Row>
                )}

                <Row className="mt-4 mb-3 llm-visibility-scheduled-actions">
                  <Col className="text-right">
                    {isEdit && questionsOnly && (
                      <Button color="secondary" type="button" className="mr-3" onClick={generateQuestions}>
                        {questionsGenerated ? "Recreate Questions" : "Generate Questions"}
                      </Button>
                    )}
                    {isEdit && questionsOnly && questionsGenerated && (
                      <Button color="success" type="button" className="mr-3" onClick={activateAnswers}>
                        Start Scheduled Answers
                      </Button>
                    )}
                    <Button color="info" size="lg" type="submit">
                      Save Schedule
                    </Button>
                  </Col>
                </Row>
              </Form>
            )}
            <ToastContainer />
          </CardBody>
        </Card>
      </div>
    </Header>
  );
};

export default LLMVisibilityScheduledForm;
